import { asText } from '@prismicio/client';

import type { KeyTextField, RichTextField, RTImageNode, RTNode } from '@prismicio/client';

/**
 * An answer label is rich text, and staff author it as a **picture** as often
 * as words: "which flag marks the safest place to swim" is three photographs,
 * not three sentences — 147 of the production repository's 650-odd quiz answers
 * and every one of its checklist items carry an image.
 *
 * These two helpers are the only place that distinction is made, so the chip
 * the learner taps, the answer echoed back into the thread and the reducer that
 * marks it all agree on what an option says.
 */

/** The picture an option carries, or `null` when it is words alone. */
export function labelImage(label: RichTextField | KeyTextField): RTImageNode | null {
  if (label === null || typeof label === 'string') {
    return null;
  }

  // `RichTextField` is a union of tuple shapes, which defeats `find`'s type
  // guard overload; one widening step and the narrowing works as written.
  const nodes: ReadonlyArray<RTNode> = label;

  return nodes.find((node): node is RTImageNode => node.type === 'image') ?? null;
}

/** An option in words: what it says, or the alt text of the picture it is. */
export function labelText(label: RichTextField | KeyTextField): string {
  if (label === null) {
    return '';
  }

  if (typeof label === 'string') {
    return label.trim();
  }

  const text = asText(label).trim();

  return text === '' ? (labelImage(label)?.alt ?? '').trim() : text;
}
